import { useCallback, useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
} from "firebase/auth";

import { setAuthToken } from "../api/client";
import type { AuthState, SignedInUser } from "./auth";
import { AuthContext, toUser } from "./auth";
import { firebaseConfigured, getFirebaseAuth } from "./firebase";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SignedInUser | null>(null);
  // Nothing to wait for when there is no Firebase project to ask.
  const [loading, setLoading] = useState(firebaseConfigured);

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) return;

    const stopAuth = onAuthStateChanged(auth, (u) => {
      setUser(u ? toUser(u) : null);
      setLoading(false);
    });

    // Fires on sign in, sign out, and every hourly refresh. Firebase ID
    // tokens expire after an hour, so reading the token once would leave a
    // manager who keeps the tab open all day getting 401s by mid afternoon
    // with nothing on screen to explain why.
    const stopToken = onIdTokenChanged(auth, (u) => {
      if (!u) {
        setAuthToken(null);
        return;
      }
      void u.getIdToken().then(setAuthToken);
    });

    return () => {
      stopAuth();
      stopToken();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase is not configured in this build.");
    await signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth();
    if (!auth) return;
    await fbSignOut(auth);
    setAuthToken(null);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase is not configured in this build.");
    await sendPasswordResetEmail(auth, email.trim());
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      signIn,
      signOut,
      resetPassword,
    }),
    [user, loading, signIn, signOut, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
